import { Alert, Button, Space } from "antd";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { Link } from "react-router-dom";
import { getUserSearchDataObjectsPermissionRequest } from "store/dataObjectDuplicateSearch/actions";
import { getGoldenStewardshipPermissionRequest } from "store/goldenNominal/actions";

interface IMergedRecordNoticeProps {
    // What the dataset service says, e.g. "This record was merged into record <id>."
    message: string;
    // The record it was merged into, when it can be opened here.
    onOpenSurvivor?: () => void;
    // The schema's Subsumed records (review queue).
    subsumedPath?: string | null;
    onClose: () => void;
}

// PRE-2837: a link led to a record that deduplication merged into another and retired. The way to
// Subsumed records is offered only to those the review queue is open to (as for its button): to
// restore it for a steward, to see it otherwise.
const MergedRecordNotice = ({ message, onOpenSurvivor, subsumedPath, onClose }: IMergedRecordNoticeProps) => {
    const dispatch = useAppDispatch();
    const canReview = useAppSelector((state) => !!state.goldenNominal.goldenStewardshipPermission.canReview);
    const canSearch = useAppSelector((state) => !!state.dataObjectDuplicateSearch.userSearchDataObjectsPermision?.canView);

    React.useEffect(() => {
        dispatch(getGoldenStewardshipPermissionRequest());
        dispatch(getUserSearchDataObjectsPermissionRequest());
    }, [dispatch]);

    return (
        <Alert
            type="info"
            showIcon
            closable
            onClose={onClose}
            className="!mb-3"
            title={message}
            description={
                <Space wrap>
                    {onOpenSurvivor && (
                        <Button size="small" type="primary" onClick={onOpenSurvivor} data-testid="merged-record-open-survivor">
                            Open the record it was merged into
                        </Button>
                    )}
                    {subsumedPath && (canReview || canSearch) && (
                        <Link to={subsumedPath} data-testid="merged-record-subsumed-link">
                            {canReview ? "Restore it from Subsumed records" : "See it in Subsumed records"}
                        </Link>
                    )}
                </Space>
            }
            data-testid="merged-record-notice"
        />
    );
};

export default MergedRecordNotice;
